import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Collapse,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Delete,
  Factory,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocalShipping,
  Print,
  Refresh,
  ShoppingCart,
} from '@mui/icons-material';
import axios from 'axios';
import { Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader/PageHeader';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiError = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const message = data?.message || data?.error?.message || data?.error;
  if (Array.isArray(message)) return message.join('; ');
  return typeof message === 'string' ? message : fallback;
};

interface OrderLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  itemDescription?: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice?: number;
  status: string;
  workOrderId?: string;
  workOrderNumber?: string;
}

interface CustomerOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  customerContact?: string;
  customerReference?: string;
  status: string;
  orderDate: string;
  dueDate?: string;
  notes?: string;
  lines: OrderLine[];
}

interface ItemOption {
  id: string;
  itemCode: string;
  description: string;
  unitOfMeasure: string;
}

interface NewLine {
  itemId: string;
  quantity: string;
  unitPrice: string;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default',
  confirmed: 'info',
  in_production: 'warning',
  fulfilled: 'success',
  cancelled: 'error',
  open: 'default',
};

const CustomerOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [items, setItems] = useState<ItemOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    customerName: '',
    customerContact: '',
    customerReference: '',
    dueDate: '',
    notes: '',
  });
  const [newLines, setNewLines] = useState<NewLine[]>([{ itemId: '', quantity: '', unitPrice: '' }]);

  const notify = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_BASE}/work-orders/customer-orders${params}`, {
        headers: authHeaders(),
      });
      setOrders(response.data?.data || []);
    } catch (err) {
      notify(apiError(err, 'Failed to load customer orders'), 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/master-data/items`, { headers: authHeaders() });
      const list = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setItems(
        list
          .filter((i: any) => i.itemType === 'finished_good' && i.status === 'active')
          .map((i: any) => ({
            id: i.id,
            itemCode: i.itemCode,
            description: i.description,
            unitOfMeasure: i.unitOfMeasure || 'PC',
          })),
      );
    } catch {
      /* item list is only needed for the create dialog */
    }
  }, []);

  useEffect(() => {
    load();
    loadItems();
  }, [load, loadItems]);

  const action = async (key: string, fn: () => Promise<void>, successMessage: string) => {
    setBusy(key);
    try {
      await fn();
      notify(successMessage);
      await load();
    } catch (err) {
      notify(apiError(err, 'Action failed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleCreate = () =>
    action(
      'create',
      async () => {
        const lines = newLines
          .filter((l) => l.itemId && Number(l.quantity) > 0)
          .map((l) => {
            const item = items.find((i) => i.id === l.itemId)!;
            return {
              itemId: item.id,
              itemCode: item.itemCode,
              itemDescription: item.description,
              quantity: Number(l.quantity),
              unitOfMeasure: item.unitOfMeasure,
              ...(l.unitPrice ? { unitPrice: Number(l.unitPrice) } : {}),
            };
          });
        if (!form.customerName || lines.length === 0) {
          throw new Error('Customer name and at least one line are required');
        }
        await axios.post(
          `${API_BASE}/work-orders/customer-orders`,
          {
            customerName: form.customerName,
            customerContact: form.customerContact || undefined,
            customerReference: form.customerReference || undefined,
            dueDate: form.dueDate || undefined,
            notes: form.notes || undefined,
            createdBy: localStorage.getItem('userEmail') || 'sales-desk',
            lines,
          },
          { headers: authHeaders() },
        );
        setCreateOpen(false);
        setForm({ customerName: '', customerContact: '', customerReference: '', dueDate: '', notes: '' });
        setNewLines([{ itemId: '', quantity: '', unitPrice: '' }]);
      },
      'Customer order created (draft)',
    );

  const confirmOrder = (order: CustomerOrder) =>
    action(
      `confirm-${order.id}`,
      async () => {
        await axios.post(
          `${API_BASE}/work-orders/customer-orders/${order.id}/confirm`,
          { confirmedBy: localStorage.getItem('userEmail') || 'sales-desk' },
          { headers: authHeaders() },
        );
      },
      `Order ${order.orderNumber} confirmed`,
    );

  const createWorkOrder = (order: CustomerOrder, line: OrderLine) =>
    action(
      `wo-${line.id}`,
      async () => {
        await axios.post(
          `${API_BASE}/work-orders/customer-orders/${order.id}/lines/${line.id}/create-work-order`,
          { createdBy: localStorage.getItem('userEmail') || 'planner' },
          { headers: authHeaders() },
        );
      },
      `Work order created for line ${line.lineNumber} (${line.itemCode})`,
    );

  const fulfillOrder = (order: CustomerOrder) =>
    action(
      `fulfill-${order.id}`,
      async () => {
        await axios.post(
          `${API_BASE}/work-orders/customer-orders/${order.id}/fulfill`,
          { userId: localStorage.getItem('userEmail') || 'sales-desk' },
          { headers: authHeaders() },
        );
      },
      `Order ${order.orderNumber} fulfilled`,
    );

  const statusChip = (status: string) => (
    <Chip label={status.replace(/_/g, ' ').toUpperCase()} color={STATUS_COLORS[status] || 'default'} size="small" />
  );

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
      <PageHeader
        section="Sales"
        title="Customer Orders"
        subtitle="Sales orders → confirmed → work orders per line → fulfilled and delivered."
        actions={
          <>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{
                minWidth: 150,
                '& .MuiOutlinedInput-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.65)' },
                '& .MuiSvgIcon-root': { color: '#fff' },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {['draft', 'confirmed', 'in_production', 'fulfilled', 'cancelled'].map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </TextField>
            <Button startIcon={<Refresh />} onClick={load} variant="outlined">
              Refresh
            </Button>
            <Button
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}
              variant="contained"
              sx={{ bgcolor: '#f2b32c', color: '#1a1a1a', fontWeight: 700, '&:hover': { bgcolor: '#e0a41f' } }}
            >
              New Order
            </Button>
          </>
        }
      />

      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : orders.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography color="text.secondary">
                No customer orders{statusFilter ? ` with status "${statusFilter}"` : ''}. Create one with “New Order”.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>Order #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Lines</TableCell>
                    <TableCell>Due date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <React.Fragment key={order.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setExpanded((e) => ({ ...e, [order.id]: !e[order.id] }))}
                          >
                            {expanded[order.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>{order.orderNumber}</Typography>
                        </TableCell>
                        <TableCell>{order.customerName}</TableCell>
                        <TableCell>{order.customerReference || '—'}</TableCell>
                        <TableCell>
                          {(order.lines || []).length} line(s) ·{' '}
                          {(order.lines || [])
                            .map((l) => `${Number(l.quantity)} ${l.unitOfMeasure} ${l.itemCode}`)
                            .join(', ')
                            .slice(0, 60)}
                        </TableCell>
                        <TableCell>{order.dueDate ? String(order.dueDate).slice(0, 10) : '—'}</TableCell>
                        <TableCell>{statusChip(order.status)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            title="Print order / delivery note"
                            onClick={() => window.open(`${API_BASE}/work-orders/customer-orders/${order.id}/report`, '_blank')}
                          >
                            <Print fontSize="small" />
                          </IconButton>
                          {order.status === 'draft' && (
                            <Button
                              size="small"
                              startIcon={<CheckCircle />}
                              disabled={busy === `confirm-${order.id}`}
                              onClick={() => confirmOrder(order)}
                            >
                              Confirm
                            </Button>
                          )}
                          {order.status === 'in_production' && (
                            <Button
                              size="small"
                              color="success"
                              startIcon={<LocalShipping />}
                              disabled={busy === `fulfill-${order.id}`}
                              onClick={() => fulfillOrder(order)}
                            >
                              Fulfill
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                          <Collapse in={!!expanded[order.id]} timeout="auto" unmountOnExit>
                            <Box sx={{ m: 1, ml: 6 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">Unit price</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Work order</TableCell>
                                    <TableCell align="right" />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(order.lines || []).map((line) => (
                                    <TableRow key={line.id}>
                                      <TableCell>{line.lineNumber}</TableCell>
                                      <TableCell>
                                        {line.itemCode}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {line.itemDescription}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        {Number(line.quantity)} {line.unitOfMeasure}
                                      </TableCell>
                                      <TableCell align="right">
                                        {line.unitPrice ? Number(line.unitPrice).toLocaleString() : '—'}
                                      </TableCell>
                                      <TableCell>{statusChip(line.status)}</TableCell>
                                      <TableCell>
                                        {line.workOrderId ? (
                                          <MuiLink
                                            component="button"
                                            underline="hover"
                                            sx={{ fontWeight: 700, cursor: 'pointer' }}
                                            onClick={() => navigate(`/work-orders/${line.workOrderId}`)}
                                          >
                                            {line.workOrderNumber}
                                          </MuiLink>
                                        ) : ('—')}
                                        {line.workOrderId && (
                                          <IconButton
                                            size="small"
                                            title="Print job card"
                                            onClick={() => window.open(`${API_BASE}/work-orders/${line.workOrderId}/report`, '_blank')}
                                          >
                                            <Print sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {['confirmed', 'in_production'].includes(order.status) &&
                                          line.status === 'open' && (
                                            <Button
                                              size="small"
                                              startIcon={<Factory />}
                                              disabled={busy === `wo-${line.id}`}
                                              onClick={() => createWorkOrder(order, line)}
                                            >
                                              Create Work Order
                                            </Button>
                                          )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Customer Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Customer name"
                value={form.customerName}
                onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Customer contact (email/phone)"
                value={form.customerContact}
                onChange={(e) => setForm({ ...form, customerContact: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Customer reference (LPO no.)"
                value={form.customerReference}
                onChange={(e) => setForm({ ...form, customerReference: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Due date"
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Order lines
              </Typography>
              {newLines.map((line, index) => (
                <Box key={index} display="flex" gap={1} mb={1} alignItems="center">
                  <TextField
                    select
                    label="Item (finished goods)"
                    value={line.itemId}
                    onChange={(e) => {
                      const next = [...newLines];
                      next[index] = { ...line, itemId: e.target.value };
                      setNewLines(next);
                    }}
                    sx={{ flex: 2 }}
                    size="small"
                  >
                    {items.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.itemCode} — {item.description}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Quantity"
                    type="number"
                    value={line.quantity}
                    onChange={(e) => {
                      const next = [...newLines];
                      next[index] = { ...line, quantity: e.target.value };
                      setNewLines(next);
                    }}
                    sx={{ flex: 1 }}
                    size="small"
                  />
                  <TextField
                    label="Unit price"
                    type="number"
                    value={line.unitPrice}
                    onChange={(e) => {
                      const next = [...newLines];
                      next[index] = { ...line, unitPrice: e.target.value };
                      setNewLines(next);
                    }}
                    sx={{ flex: 1 }}
                    size="small"
                  />
                  <IconButton
                    onClick={() => setNewLines(newLines.filter((_, i) => i !== index))}
                    disabled={newLines.length === 1}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => setNewLines([...newLines, { itemId: '', quantity: '', unitPrice: '' }])}
              >
                Add line
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={busy === 'create'}>
            {busy === 'create' ? <CircularProgress size={20} /> : 'Create Order'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CustomerOrders;
